import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { ArrowLeft, ArrowRight, CalendarBlank } from './icons'
import { addDays, addMonths, compareDates, formatDisplayDate, formatLongDate, formatMonth, fromIsoDate, monthGrid, parseTypedDate, toIsoDate, todayParts, type DateParts } from './date-logic'

export type DatePickerProps = {
  /** Field passes the id, aria-describedby and aria-invalid; they land on the text input. */
  id?: string
  /** An ISO date ("2026-11-01") or null for no date. */
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  /** Earliest and latest dates the calendar offers, as ISO dates. */
  min?: string | null
  max?: string | null
  disabled?: boolean
  'aria-describedby'?: string
  'aria-invalid'?: boolean | 'true' | 'false'
  'aria-label'?: string
  /** The field's name for the calendar button and popup, for example "End Date". */
  name?: string
  className?: string
}

const WEEKDAYS: Array<[string, string]> = [['Su', 'Sunday'], ['Mo', 'Monday'], ['Tu', 'Tuesday'], ['We', 'Wednesday'], ['Th', 'Thursday'], ['Fr', 'Friday'], ['Sa', 'Saturday']]

/**
 * A date field: type a date ("Nov 1, 2026", "11/1/2026") or pick one from
 * the calendar. The field shows dates as "Nov 1, 2026" and stores ISO dates.
 * Calendar keys: arrows move a day or a week, Page Up and Page Down move a
 * month, Home and End go to the start and end of the week, Enter picks,
 * Escape closes and returns focus to the calendar button.
 */
export function DatePicker({ id, value, onChange, placeholder = 'Select a date', min = null, max = null, disabled, name, className = '', ...aria }: DatePickerProps) {
  const generatedId = useId()
  const inputId = id ?? `${generatedId}-input`
  const popoverId = `${generatedId}-calendar`
  const monthId = `${generatedId}-month`
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const gridRef = useRef<HTMLTableElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const focusDayRef = useRef(false)
  const [text, setText] = useState(() => formatDisplayDate(value))
  const [typing, setTyping] = useState(false)
  const [open, setOpen] = useState(false)
  const selected = fromIsoDate(value)
  const minParts = fromIsoDate(min)
  const maxParts = fromIsoDate(max)
  const clamp = (parts: DateParts) => minParts && compareDates(parts, minParts) < 0 ? minParts : maxParts && compareDates(parts, maxParts) > 0 ? maxParts : parts
  const [focused, setFocused] = useState<DateParts>(() => clamp(selected || todayParts()))
  const today = todayParts()

  // The field follows the stored value unless the person is typing in it.
  useEffect(() => { if (!typing) setText(formatDisplayDate(value)) }, [value, typing])

  // Close on a click or tap outside the field and its calendar.
  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent | TouchEvent) => { if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', close)
    document.addEventListener('touchstart', close)
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('touchstart', close) }
  }, [open])

  // Move focus to the focused day after the calendar opens or a key moves it.
  useLayoutEffect(() => {
    if (!open || !focusDayRef.current) return
    focusDayRef.current = false
    gridRef.current?.querySelector<HTMLButtonElement>('button[tabindex="0"]')?.focus()
  })

  const outOfRange = (parts: DateParts) => Boolean((minParts && compareDates(parts, minParts) < 0) || (maxParts && compareDates(parts, maxParts) > 0))

  function openCalendar() {
    setFocused(clamp(selected || today))
    focusDayRef.current = true
    setOpen(true)
  }
  function closeCalendar(returnFocus: boolean) {
    setOpen(false)
    if (returnFocus) toggleRef.current?.focus()
  }
  function pick(parts: DateParts) {
    if (outOfRange(parts)) return
    onChange(toIsoDate(parts))
    setTyping(false)
    setText(formatDisplayDate(toIsoDate(parts)))
    setOpen(false)
    inputRef.current?.focus()
  }
  function commitTyped() {
    const parsed = parseTypedDate(text)
    setTyping(false)
    if (parsed === undefined || (parsed && outOfRange(fromIsoDate(parsed)!))) { setText(formatDisplayDate(value)); return }
    if (parsed !== value) onChange(parsed)
    setText(formatDisplayDate(parsed))
  }
  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') { event.preventDefault(); commitTyped() }
    else if (event.key === 'ArrowDown' && (event.altKey || !open)) { event.preventDefault(); openCalendar() }
    else if (event.key === 'Escape' && open) { event.preventDefault(); setOpen(false) }
  }
  function onGridKeyDown(event: KeyboardEvent<HTMLTableElement>) {
    const moves: Record<string, (parts: DateParts) => DateParts> = {
      ArrowLeft: (parts) => addDays(parts, -1),
      ArrowRight: (parts) => addDays(parts, 1),
      ArrowUp: (parts) => addDays(parts, -7),
      ArrowDown: (parts) => addDays(parts, 7),
      PageUp: (parts) => addMonths(parts, event.shiftKey ? -12 : -1),
      PageDown: (parts) => addMonths(parts, event.shiftKey ? 12 : 1),
      Home: (parts) => addDays(parts, -new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()),
      End: (parts) => addDays(parts, 6 - new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()),
    }
    const move = moves[event.key]
    if (!move) return
    event.preventDefault()
    focusDayRef.current = true
    setFocused((current) => clamp(move(current)))
  }
  function moveMonth(months: number) {
    setFocused((current) => clamp(addMonths(current, months)))
  }

  const weeks = monthGrid(focused.year, focused.month)
  const label = aria['aria-label']
  const fieldName = name || label || 'a date'

  return (
    <div ref={rootRef} className={['ui-date-picker', className].filter(Boolean).join(' ')}>
      <input
        ref={inputRef}
        id={inputId}
        className="ui-input ui-date-picker__input"
        type="text"
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        value={text}
        disabled={disabled}
        aria-label={label}
        aria-describedby={aria['aria-describedby']}
        aria-invalid={aria['aria-invalid']}
        onChange={(event) => { setTyping(true); setText(event.target.value) }}
        onBlur={(event) => { if (!popoverRef.current?.contains(event.relatedTarget as Node)) commitTyped() }}
        onKeyDown={onInputKeyDown}
      />
      <button
        ref={toggleRef}
        type="button"
        className="ui-icon-button ui-date-picker__toggle"
        aria-label={open ? 'Close calendar' : `Choose ${fieldName} from a calendar`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        disabled={disabled}
        onClick={() => (open ? closeCalendar(false) : openCalendar())}
      >
        <CalendarBlank size={18} aria-hidden="true" />
      </button>
      {open && (
        <div
          ref={popoverRef}
          id={popoverId}
          className="ui-date-picker__popover"
          role="dialog"
          aria-modal="false"
          aria-label={`Choose ${fieldName}`}
          onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeCalendar(true) } }}
          onBlur={(event) => { if (!rootRef.current?.contains(event.relatedTarget as Node)) setOpen(false) }}
        >
          <div className="ui-date-picker__head">
            <button type="button" className="ui-icon-button" aria-label="Previous month" onClick={() => moveMonth(-1)}><ArrowLeft size={16} aria-hidden="true" /></button>
            <div id={monthId} className="ui-date-picker__month" aria-live="polite">{formatMonth(focused.year, focused.month)}</div>
            <button type="button" className="ui-icon-button" aria-label="Next month" onClick={() => moveMonth(1)}><ArrowRight size={16} aria-hidden="true" /></button>
          </div>
          <table ref={gridRef} className="ui-date-picker__grid" aria-labelledby={monthId} onKeyDown={onGridKeyDown}>
            <thead>
              <tr>{WEEKDAYS.map(([short, long]) => <th key={short} scope="col" abbr={long}>{short}</th>)}</tr>
            </thead>
            <tbody>
              {weeks.map((week) => (
                <tr key={toIsoDate(week[0])}>
                  {week.map((day) => {
                    const iso = toIsoDate(day)
                    const isFocused = compareDates(day, focused) === 0
                    const isSelected = Boolean(selected && compareDates(day, selected) === 0)
                    const isToday = compareDates(day, today) === 0
                    const blocked = outOfRange(day)
                    return (
                      <td key={iso}>
                        <button
                          type="button"
                          tabIndex={isFocused ? 0 : -1}
                          className={['ui-date-picker__day', day.month !== focused.month && 'is-outside', isSelected && 'is-selected', isToday && 'is-today'].filter(Boolean).join(' ')}
                          aria-label={`${formatLongDate(day)}${isToday ? ', today' : ''}`}
                          aria-pressed={isSelected}
                          aria-disabled={blocked || undefined}
                          onClick={() => pick(day)}
                        >
                          {day.day}
                        </button>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {value && (
            <div className="ui-date-picker__foot">
              <button type="button" className="link-button" onClick={() => { onChange(null); setText(''); setOpen(false); inputRef.current?.focus() }}>Clear Date</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
